(function () {
  'use strict';

  if (window.__skinAdvisorBannerInitialized) return;
  window.__skinAdvisorBannerInitialized = true;

  const REF_CODE_PATTERN = /^[^\u0000-\u001F\u007F]+$/u;
  const CACHE_PREFIX = 'skin-advisor-banner:v1:';
  const ACTIVE_REF_STORAGE_KEY = 'skin-advisor-banner:active-ref';

  let activeRefCode = '';
  let requestId = 0;

  function getRoot() {
    return document.querySelector('[data-skin-advisor-banner]');
  }

  function normalizeRefCode(value) {
    const refCode = typeof value === 'string' ? value.trim().normalize('NFC') : '';
    return refCode.length <= 100 && REF_CODE_PATTERN.test(refCode) ? refCode : '';
  }

  function getUrlRefCode() {
    const match = window.location.search.match(/[?&]ref=([^&]*)/i);
    if (!match) return '';

    try {
      // En una query, "+" representa un espacio. Un signo "+" real debe llegar
      // codificado como %2B, que decodeURIComponent conserva correctamente.
      return normalizeRefCode(decodeURIComponent(match[1].replace(/\+/g, ' ')));
    } catch (error) {
      return '';
    }
  }

  function getGoAffProRefCode() {
    if (typeof window.gfpGetCookie !== 'function') return '';

    try {
      return normalizeRefCode(window.gfpGetCookie('ref'));
    } catch (error) {
      return '';
    }
  }

  function getSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setSessionValue(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // El banner funciona también cuando el almacenamiento está bloqueado.
    }
  }

  function resolveRefCode(candidateRefCode) {
    const urlRefCode = getUrlRefCode();
    if (urlRefCode) {
      setSessionValue(ACTIVE_REF_STORAGE_KEY, urlRefCode);
      return urlRefCode;
    }

    const storedRefCode = normalizeRefCode(getSessionValue(ACTIVE_REF_STORAGE_KEY));
    if (storedRefCode) return storedRefCode;

    const candidate = normalizeRefCode(candidateRefCode);
    if (candidate) {
      setSessionValue(ACTIVE_REF_STORAGE_KEY, candidate);
      return candidate;
    }

    const cookieRefCode = getGoAffProRefCode();
    if (cookieRefCode) setSessionValue(ACTIVE_REF_STORAGE_KEY, cookieRefCode);
    return cookieRefCode;
  }

  function getInitials(advisor) {
    const parts = [advisor.firstName, advisor.lastName]
      .filter((part) => typeof part === 'string' && part.trim())
      .map((part) => part.trim().charAt(0));

    if (!parts.length && typeof advisor.name === 'string') {
      return advisor.name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase() || 'SA';
    }

    return parts.slice(0, 2).join('').toUpperCase() || 'SA';
  }

  function formatPhoneNumber(phone) {
    const digits = String(phone || '').replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('34')) {
      return '+34 ' + digits.slice(2, 5) + ' ' + digits.slice(5, 8) + ' ' + digits.slice(8);
    }

    return phone;
  }

  function resetContactLink(link) {
    if (!link) return;
    link.hidden = true;
    link.removeAttribute('href');
    link.removeAttribute('aria-label');
    link.removeAttribute('title');
  }

  function syncBannerOffset(root) {
    if (!root || root.hidden) {
      document.documentElement.classList.remove('skin-advisor-banner-visible');
      document.documentElement.style.removeProperty('--skin-advisor-banner-height');
      return;
    }

    document.documentElement.classList.add('skin-advisor-banner-visible');
    document.documentElement.style.setProperty('--skin-advisor-banner-height', root.offsetHeight + 'px');
  }

  function hideBanner(root) {
    if (root) root.hidden = true;
    document.documentElement.classList.remove('skin-advisor-banner-is-active');
    syncBannerOffset(root);
  }

  function renderAdvisor(root, refCode, advisor) {
    if (!root || !advisor || typeof advisor !== 'object') return false;

    const name = typeof advisor.name === 'string' ? advisor.name.trim() : '';
    if (!name) return false;

    const nameElement = root.querySelector('[data-skin-advisor-name]');
    const photo = root.querySelector('[data-skin-advisor-photo]');
    const fallback = root.querySelector('[data-skin-advisor-avatar-fallback]');
    const whatsapp = root.querySelector('[data-skin-advisor-whatsapp]');
    const whatsappLabel = root.querySelector('[data-skin-advisor-whatsapp-label]');
    const email = root.querySelector('[data-skin-advisor-email]');
    const emailLabel = root.querySelector('[data-skin-advisor-email-label]');

    if (!nameElement || !photo || !fallback) return false;

    nameElement.textContent = name;
    fallback.textContent = getInitials(advisor);
    fallback.hidden = false;
    photo.hidden = true;
    photo.removeAttribute('src');
    photo.alt = '';

    const profilePhoto = typeof advisor.profilePhoto === 'string' ? advisor.profilePhoto.trim() : '';
    if (/^https:\/\//i.test(profilePhoto)) {
      photo.onload = function () {
        photo.hidden = false;
        photo.alt = 'Foto de ' + name;
        fallback.hidden = true;
      };
      photo.onerror = function () {
        photo.hidden = true;
        photo.removeAttribute('src');
        fallback.hidden = false;
      };
      photo.src = profilePhoto;
    }

    resetContactLink(whatsapp);
    resetContactLink(email);

    const advisorPhone = typeof advisor.phone === 'string' ? advisor.phone.trim() : '';
    const phoneDigits = advisorPhone.replace(/\D/g, '');
    if (advisorPhone && phoneDigits) {
      if (whatsapp) {
        whatsapp.href = 'https://wa.me/' + phoneDigits;
        whatsapp.setAttribute('aria-label', 'Contactar con ' + name + ' por WhatsApp');
        whatsapp.title = 'Escribir por WhatsApp al ' + formatPhoneNumber(advisorPhone);
        whatsapp.hidden = false;
      }
      if (whatsappLabel) {
        whatsappLabel.textContent = formatPhoneNumber(advisorPhone);
      }
    }

    const advisorEmail = typeof advisor.email === 'string' ? advisor.email.trim() : '';
    if (email && advisorEmail) {
      email.href = 'https://mail.google.com/mail/?view=cm&fs=1&to=' + encodeURIComponent(advisorEmail);
      email.setAttribute('aria-label', 'Enviar un email a ' + name);
      email.title = 'Enviar un email a ' + advisorEmail;
      email.hidden = false;
      if (emailLabel) emailLabel.textContent = advisorEmail;
    }

    root.hidden = false;
    document.documentElement.classList.add('skin-advisor-banner-is-active');
    window.requestAnimationFrame(function () {
      syncBannerOffset(root);
    });
    return true;
  }

  async function loadAdvisor(candidateRefCode) {
    const root = getRoot();
    if (!root) return;

    const refCode = resolveRefCode(candidateRefCode);
    if (!refCode) {
      activeRefCode = '';
      hideBanner(root);
      return;
    }

    if (refCode === activeRefCode && !root.hidden) return;
    activeRefCode = refCode;
    const currentRequestId = ++requestId;

    let renderedFromCache = false;
    const cachedValue = getSessionValue(CACHE_PREFIX + refCode);
    if (cachedValue) {
      try {
        renderedFromCache = renderAdvisor(root, refCode, JSON.parse(cachedValue));
      } catch (error) {
        // Una entrada de caché inválida se sustituye con la respuesta actual.
      }
    }

    const endpoint = root.getAttribute('data-endpoint');
    if (!endpoint) return;

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('ref_code', refCode);
      url.searchParams.set('contact_version', '2');

      const response = await window.fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (currentRequestId !== requestId || refCode !== activeRefCode) return;
      if (!response.ok) {
        if (!renderedFromCache) hideBanner(root);
        return;
      }

      const data = await response.json();
      const advisor = data && data.advisor ? data.advisor : data;
      if (!renderAdvisor(root, refCode, advisor)) {
        if (!renderedFromCache) hideBanner(root);
        return;
      }

      setSessionValue(CACHE_PREFIX + refCode, JSON.stringify(advisor));
    } catch (error) {
      if (currentRequestId === requestId && !renderedFromCache) {
        hideBanner(root);
      }
    }
  }

  function initialize() {
    const root = getRoot();
    if (!root) return;

    const closeButton = root.querySelector('[data-skin-advisor-close]');
    if (closeButton) {
      closeButton.addEventListener('click', function () {
        root.hidden = true;
        syncBannerOffset(root);
      });
    }

    if (typeof window.ResizeObserver === 'function') {
      const bannerResizeObserver = new window.ResizeObserver(function () {
        syncBannerOffset(root);
      });
      bannerResizeObserver.observe(root);
    } else {
      window.addEventListener('resize', function () {
        syncBannerOffset(root);
      });
    }

    window.addEventListener('goaffproScriptLoaded', function () {
      loadAdvisor(getGoAffProRefCode());
    });

    window.addEventListener('goaffproCookieSet', function (event) {
      if (!event.detail || event.detail.key !== 'ref') return;
      loadAdvisor(event.detail.value);
    });

    window.addEventListener('goaffproReferralCode', function (event) {
      if (!event.detail || typeof event.detail.ref === 'undefined') return;
      loadAdvisor(event.detail.ref);
    });

    loadAdvisor(getGoAffProRefCode());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
